import { useEffect, useRef, useState } from 'react';
import { ChevronsUpDown, Building2, Check } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { PLANS } from '../lib/plans';
import { toast } from 'sonner';

/**
 * Combobox de organización activa en el sidebar. Solo aparece si el usuario
 * tiene 2+ membresías. Cambiar de org emite un nuevo JWT y recarga la vista.
 */
export default function OrgSwitcher() {
  const { organization, memberships, selectOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const multi = memberships && memberships.length > 1;
  const plan = PLANS[organization?.plan || 'pyme'];

  const pick = async (m) => {
    if (m.organization_id === organization?.id) { setOpen(false); return; }
    setSwitching(m.organization_id);
    try {
      await selectOrg(m.organization_id);
      toast.success(`Cambiado a ${m.razon_social}`);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pudimos cambiar de organización');
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div ref={ref} className="relative" data-testid="org-switcher">
      <button
        onClick={() => multi && setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 rounded-md px-3 py-2 hover:bg-white/[0.04] transition-colors ${!multi ? 'cursor-default' : ''}`}
        data-testid="org-switcher-toggle"
      >
        <div className="w-8 h-8 rounded-md bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
          {organization?.logo_url ? (
            <img src={organization.logo_url} alt="" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <Building2 size={14} className="text-white/70" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[13px] font-medium text-white truncate">{organization?.razon_social || '—'}</div>
          <div className="text-[11px] text-white/75 truncate flex items-center gap-1.5">
            <span>{organization?.rut || 'sin RUT'}</span>
            <span className="text-white/40">·</span>
            <span className="uppercase tracking-wider font-semibold" style={{ color: plan?.color === '#0B1220' ? '#93C5FD' : (plan?.color || '#7EE0AC') }}>{plan?.name}</span>
          </div>
        </div>
        {multi && <ChevronsUpDown size={14} className="text-white/75 shrink-0" />}
      </button>

      {open && multi && (
        <div
          className="absolute left-0 right-0 top-full mt-2 z-30 bg-[#132038] border border-white/10 shadow-xl max-h-[380px] overflow-y-auto"
          data-testid="org-switcher-menu"
        >
          <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-white/60">
            Cambiar de organización ({memberships.length})
          </div>
          {memberships.map((m) => {
            const p = PLANS[m.plan || 'pyme'];
            const active = m.organization_id === organization?.id;
            const isSwitching = switching === m.organization_id;
            return (
              <button
                key={m.organization_id}
                onClick={() => pick(m)}
                disabled={isSwitching}
                className={`w-full px-3 py-2.5 text-left hover:bg-white/[0.04] flex items-center gap-3 ${active ? 'bg-white/[0.03]' : ''}`}
                data-testid={`org-switcher-item-${m.organization_id}`}
              >
                <div className="w-6 h-6 rounded-md bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0">
                  <Building2 size={11} className="text-white/70" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium text-white truncate">{m.razon_social}</div>
                  <div className="text-[10.5px] text-white/60 truncate">
                    {m.role} · Plan {p?.name}
                  </div>
                </div>
                {active && <Check size={12} className="text-[#7EE0AC] shrink-0" strokeWidth={2.5} />}
                {isSwitching && <span className="text-[10px] text-white/60">…</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
